import { useEffect, useRef, useState } from "react";

const CLIENT = "ca-pub-8402662199094698";
const SLOT = "9485429112";
const STORAGE_KEY = "terella-ads-consent";

type Choice = "granted" | "denied";

declare global {
  interface Window {
    adsbygoogle?: Array<Record<string, unknown>>;
  }
}

let adsScript: Promise<void> | null = null;

function loadAdsScript() {
  if (adsScript) return adsScript;
  adsScript = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT}`;
    script.crossOrigin = "anonymous";
    script.onload = () => resolve();
    script.onerror = () => {
      adsScript = null;
      reject(new Error("AdSense script failed"));
    };
    document.head.appendChild(script);
  });
  return adsScript;
}

function readChoice(): Choice | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function AdSlot() {
  const mountRef = useRef<HTMLDivElement>(null);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setChoice(readChoice());
    setReady(true);
  }, []);

  useEffect(() => {
    if (choice !== "granted") return;
    const mount = mountRef.current;
    if (!mount || mount.dataset.filled === "true") return;
    let cancelled = false;
    loadAdsScript()
      .then(() => {
        if (cancelled || !mount.isConnected || mount.dataset.filled === "true") return;
        const ins = document.createElement("ins");
        ins.className = "adsbygoogle";
        ins.style.display = "block";
        ins.setAttribute("data-ad-client", CLIENT);
        ins.setAttribute("data-ad-slot", SLOT);
        ins.setAttribute("data-ad-format", "auto");
        ins.setAttribute("data-full-width-responsive", "true");
        mount.appendChild(ins);
        mount.dataset.filled = "true";
        window.adsbygoogle = window.adsbygoogle || [];
        try {
          window.adsbygoogle.push({});
        } catch {
          setFailed(true);
        }
      })
      .catch(() => {
        setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [choice]);

  function save(next: Choice) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The ad stays off when storage is blocked.
    }
    setChoice(next);
  }

  if (!ready || choice === "denied" || failed) return null;

  return (
    <section className="site-ad" aria-label="Advertisement">
      {choice === "granted" ? (
        <>
          <p className="site-ad-label">Advertisement</p>
          <div ref={mountRef} />
        </>
      ) : (
        <p className="site-ad-choice">
          Allow a Google ad on this page?
          <button type="button" onClick={() => save("granted")}>
            Allow
          </button>
          <button type="button" onClick={() => save("denied")}>
            No thanks
          </button>
          <a href="/privacy">Privacy</a>
        </p>
      )}
    </section>
  );
}
